import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './public/site.css';
import PublicHeader from './public/PublicHeader';
import PublicFooter from './public/PublicFooter';
import { SiteLang } from './public/siteConfig';

// Public account-deletion page required by Google Play's Data safety form.
// It names the app + developer, shows the deletion steps, and lists what is deleted vs. retained.

const APP_NAME = 'Fconnect';
const SUPPORT_EMAIL = 'trile@fconnect.vn';
const SUPPORT_PHONE = '02513684266';

type Role = 'customer' | 'provider';

const COPY = {
  vi: {
    docTitle: 'Xóa tài khoản - Fconnect',
    title: 'Yêu cầu xóa tài khoản Fconnect',
    subtitle: (
      <>
        Trang này hướng dẫn người dùng ứng dụng <strong>{APP_NAME}</strong> (nhà phát triển:{' '}
        <strong>CÔNG TY CỔ PHẦN TÀI CHÍNH HIỆP TÍN PHÁT</strong>) cách yêu cầu xóa tài khoản và dữ liệu liên quan.
      </>
    ),
    quickTitle: 'Tóm tắt',
    quick: [
      'Bạn có thể xóa tài khoản ngay trong ứng dụng, hoặc gửi yêu cầu qua biểu mẫu bên dưới / email / điện thoại.',
      'Yêu cầu được xác minh và xử lý trong vòng 7 ngày làm việc; dữ liệu bị xóa hoàn toàn trong tối đa 30 ngày.',
      'Một số dữ liệu giao dịch được lưu giữ theo quy định pháp luật (xem mục 5).',
    ],
    inAppTitle: '1. Cách 1 — Xóa tài khoản trong ứng dụng',
    inAppSteps: [
      <>Mở ứng dụng <strong>{APP_NAME}</strong> và đăng nhập vào tài khoản cần xóa.</>,
      <>Vào mục <strong>Tài khoản</strong> (biểu tượng hồ sơ).</>,
      <>Chọn <strong>Cài đặt</strong> → <strong>Xóa tài khoản</strong>.</>,
      <>Đọc lưu ý, nhập mã xác thực (OTP) hoặc mật khẩu nếu được yêu cầu, rồi bấm <strong>Xác nhận xóa</strong>.</>,
    ],
    webTitle: '2. Cách 2 — Gửi yêu cầu xóa tài khoản qua website',
    webIntro:
      'Nếu bạn không thể đăng nhập hoặc đã gỡ ứng dụng, hãy điền biểu mẫu dưới đây. Hệ thống sẽ mở ứng dụng email với nội dung yêu cầu được điền sẵn, gửi tới ' +
      SUPPORT_EMAIL +
      '.',
    form: {
      phone: 'Số điện thoại đăng ký tài khoản',
      phonePh: 'VD: 0901234567',
      email: 'Email đăng ký (nếu có)',
      emailPh: 'ban@example.com',
      name: 'Họ và tên',
      namePh: 'Nguyễn Văn A',
      role: 'Loại tài khoản',
      roles: { customer: 'Khách hàng (người đặt dịch vụ)', provider: 'Nhà cung cấp / người lao động' } as Record<Role, string>,
      reason: 'Lý do xóa (không bắt buộc)',
      confirm:
        'Tôi hiểu rằng việc xóa tài khoản là vĩnh viễn và không thể khôi phục, kể cả lịch sử đặt dịch vụ, tin nhắn và số dư chưa rút.',
      submit: 'Gửi yêu cầu xóa tài khoản',
      needContact: 'Vui lòng nhập số điện thoại hoặc email đã đăng ký.',
      needConfirm: 'Vui lòng xác nhận bạn đã hiểu hậu quả của việc xóa tài khoản.',
    },
    sentTitle: 'Ứng dụng email đã được mở',
    sentBody: (
      <>
        Hãy bấm <strong>Gửi</strong> trong ứng dụng email để hoàn tất. Nếu ứng dụng email không mở, vui lòng sao chép nội
        dung dưới đây và gửi tới <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </>
    ),
    copy: 'Sao chép nội dung',
    copied: 'Đã sao chép',
    altTitle: 'Hoặc liên hệ trực tiếp',
    altBody: 'Gửi email hoặc gọi cho bộ phận hỗ trợ, cung cấp số điện thoại/email đã đăng ký và nêu rõ “Yêu cầu xóa tài khoản Fconnect”.',
    processTitle: '3. Quy trình xử lý',
    process: [
      <><strong>Tiếp nhận:</strong> chúng tôi xác nhận đã nhận yêu cầu trong vòng 2 ngày làm việc.</>,
      <><strong>Xác minh:</strong> để bảo vệ bạn, chúng tôi xác minh quyền sở hữu tài khoản qua số điện thoại hoặc email đã đăng ký (ví dụ: gửi mã OTP hoặc gọi lại).</>,
      <><strong>Xóa tài khoản:</strong> tài khoản bị vô hiệu hóa ngay sau khi xác minh, trong vòng 7 ngày làm việc kể từ khi nhận yêu cầu.</>,
      <><strong>Xóa dữ liệu:</strong> dữ liệu cá nhân được xóa vĩnh viễn khỏi hệ thống trong tối đa 30 ngày; bản sao lưu được xóa theo chu kỳ trong tối đa 90 ngày.</>,
    ],
    deletedTitle: '4. Dữ liệu sẽ bị xóa',
    deleted: [
      'Thông tin tài khoản: họ tên, số điện thoại, email, mật khẩu và thông tin đăng nhập',
      'Hồ sơ cá nhân: ảnh đại diện, ngày sinh, giới tính, địa chỉ đã lưu',
      'Hồ sơ nhà cung cấp: giấy tờ xác minh, kỹ năng, dịch vụ đăng ký, khu vực hoạt động',
      'Tin nhắn trò chuyện giữa bạn và khách hàng / nhà cung cấp',
      'Dữ liệu vị trí, mã thông báo đẩy (push token) và thông tin thiết bị',
      'Tùy chọn cá nhân, danh sách yêu thích và lịch sử tìm kiếm',
    ],
    keptTitle: '5. Dữ liệu được lưu giữ và thời hạn',
    keptIntro: 'Một số dữ liệu phải được lưu giữ theo quy định pháp luật Việt Nam hoặc vì mục đích an toàn. Dữ liệu này không còn gắn với hồ sơ có thể truy cập của bạn và chỉ được sử dụng cho mục đích nêu dưới đây:',
    kept: [
      <><strong>Lịch sử giao dịch, đơn đặt dịch vụ, hóa đơn và lịch sử nạp/rút tiền:</strong> lưu tối đa 10 năm theo quy định pháp luật về kế toán và thuế.</>,
      <><strong>Nhật ký phục vụ phòng chống gian lận và giải quyết tranh chấp:</strong> lưu tối đa 12 tháng.</>,
      <><strong>Đánh giá, nhận xét đã đăng:</strong> được ẩn danh hóa (xóa tên và ảnh của bạn) và có thể tiếp tục hiển thị.</>,
      <><strong>Dữ liệu theo yêu cầu của cơ quan nhà nước có thẩm quyền:</strong> lưu cho đến khi hết nghĩa vụ pháp lý.</>,
    ],
    keptOutro: 'Khi hết thời hạn lưu giữ, dữ liệu trên được xóa vĩnh viễn.',
    notesTitle: '6. Lưu ý trước khi xóa',
    notes: [
      'Việc xóa tài khoản là vĩnh viễn và không thể khôi phục.',
      'Vui lòng hoàn tất hoặc hủy các đơn dịch vụ đang thực hiện trước khi gửi yêu cầu.',
      'Nhà cung cấp cần rút toàn bộ số dư ví trước khi xóa; số dư chưa rút sẽ không thể hoàn trả sau khi tài khoản bị xóa.',
      'Nếu chỉ muốn xóa một phần dữ liệu (không xóa tài khoản), hãy liên hệ bộ phận hỗ trợ.',
    ],
    contactTitle: '7. Liên hệ hỗ trợ',
    contactLabels: { email: 'Email', phone: 'Điện thoại', hours: 'Thời gian hỗ trợ' },
    hours: '8:00 - 22:00 (Thứ 2 - Chủ nhật)',
    privacyLink: 'Xem Chính sách bảo mật',
    privacyTo: '/privacy',
    mail: {
      subject: 'Yêu cầu xóa tài khoản Fconnect',
      intro: 'Tôi yêu cầu xóa tài khoản Fconnect và toàn bộ dữ liệu liên quan.',
      phone: 'Số điện thoại đăng ký',
      email: 'Email đăng ký',
      name: 'Họ và tên',
      role: 'Loại tài khoản',
      reason: 'Lý do',
      outro: 'Tôi xác nhận đã hiểu việc xóa tài khoản là vĩnh viễn.',
    },
  },
  en: {
    docTitle: 'Delete account - Fconnect',
    title: 'Delete your Fconnect account',
    subtitle: (
      <>
        This page explains how users of the <strong>{APP_NAME}</strong> app (developer:{' '}
        <strong>HIEP TIN PHAT FINANCE JOINT STOCK COMPANY</strong>) can request deletion of their account and associated data.
      </>
    ),
    quickTitle: 'Summary',
    quick: [
      'You can delete your account inside the app, or submit a request via the form below, email, or phone.',
      'Requests are verified and processed within 7 business days; data is fully deleted within 30 days at most.',
      'Some transaction records are retained as required by law (see section 5).',
    ],
    inAppTitle: '1. Option 1 — Delete your account in the app',
    inAppSteps: [
      <>Open the <strong>{APP_NAME}</strong> app and sign in to the account you want to delete.</>,
      <>Go to <strong>Account</strong> (profile icon).</>,
      <>Tap <strong>Settings</strong> → <strong>Delete account</strong>.</>,
      <>Read the notice, enter the OTP code or password if prompted, then tap <strong>Confirm deletion</strong>.</>,
    ],
    webTitle: '2. Option 2 — Request deletion on the web',
    webIntro:
      "If you can't sign in or have uninstalled the app, fill in the form below. It opens your email app with a pre-filled request addressed to " +
      SUPPORT_EMAIL +
      '.',
    form: {
      phone: 'Phone number registered to the account',
      phonePh: 'e.g. 0901234567',
      email: 'Registered email (if any)',
      emailPh: 'you@example.com',
      name: 'Full name',
      namePh: 'Jane Doe',
      role: 'Account type',
      roles: { customer: 'Customer (books services)', provider: 'Service provider / worker' } as Record<Role, string>,
      reason: 'Reason for deletion (optional)',
      confirm:
        'I understand that deleting my account is permanent and cannot be undone, including booking history, messages and any unwithdrawn balance.',
      submit: 'Submit deletion request',
      needContact: 'Please enter the phone number or email registered to your account.',
      needConfirm: 'Please confirm you understand the consequences of deleting your account.',
    },
    sentTitle: 'Your email app has been opened',
    sentBody: (
      <>
        Tap <strong>Send</strong> in your email app to finish. If no email app opened, copy the text below and send it to{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </>
    ),
    copy: 'Copy text',
    copied: 'Copied',
    altTitle: 'Or contact us directly',
    altBody: 'Email or call our support team with the phone number/email registered to your account and state "Fconnect account deletion request".',
    processTitle: '3. How we process your request',
    process: [
      <><strong>Acknowledgement:</strong> we confirm receipt of your request within 2 business days.</>,
      <><strong>Verification:</strong> to protect you, we verify account ownership via the registered phone number or email (e.g. an OTP code or a call-back).</>,
      <><strong>Account deletion:</strong> the account is deactivated right after verification, within 7 business days of your request.</>,
      <><strong>Data deletion:</strong> personal data is permanently deleted from our systems within 30 days; backups are purged on rotation within 90 days.</>,
    ],
    deletedTitle: '4. Data that will be deleted',
    deleted: [
      'Account information: name, phone number, email, password and sign-in credentials',
      'Profile: avatar, date of birth, gender, saved addresses',
      'Provider profile: verification documents, skills, registered services, service areas',
      'Chat messages between you and customers / providers',
      'Location data, push notification tokens and device information',
      'Personal preferences, favourites and search history',
    ],
    keptTitle: '5. Data we keep and for how long',
    keptIntro: 'Some data must be retained under Vietnamese law or for safety reasons. It is no longer linked to an accessible profile and is used only for the purposes below:',
    kept: [
      <><strong>Transaction, booking, invoice and top-up/withdrawal records:</strong> kept for up to 10 years as required by accounting and tax regulations.</>,
      <><strong>Logs used for fraud prevention and dispute resolution:</strong> kept for up to 12 months.</>,
      <><strong>Published ratings and reviews:</strong> anonymised (your name and photo removed) and may remain visible.</>,
      <><strong>Data requested by competent authorities:</strong> kept until the legal obligation ends.</>,
    ],
    keptOutro: 'Once the retention period ends, this data is permanently deleted.',
    notesTitle: '6. Before you delete',
    notes: [
      'Account deletion is permanent and cannot be undone.',
      'Please complete or cancel any in-progress bookings before submitting a request.',
      'Providers should withdraw their full wallet balance first; any remaining balance cannot be refunded after deletion.',
      'If you only want some of your data deleted (without deleting the account), contact our support team.',
    ],
    contactTitle: '7. Contact support',
    contactLabels: { email: 'Email', phone: 'Phone', hours: 'Support hours' },
    hours: '8:00 - 22:00 (Mon - Sun)',
    privacyLink: 'Read our Privacy Policy',
    privacyTo: '/en/privacy',
    mail: {
      subject: 'Fconnect account deletion request',
      intro: 'I request deletion of my Fconnect account and all associated data.',
      phone: 'Registered phone',
      email: 'Registered email',
      name: 'Full name',
      role: 'Account type',
      reason: 'Reason',
      outro: 'I confirm I understand that account deletion is permanent.',
    },
  },
};

const DeleteAccount: React.FC<{ lang: SiteLang }> = ({ lang }) => {
  const t = COPY[lang];
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('customer');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    document.title = t.docTitle;
  }, [t.docTitle]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() && !email.trim()) return setError(t.form.needContact);
    if (!confirmed) return setError(t.form.needConfirm);
    setError('');

    const m = t.mail;
    const body = [
      m.intro,
      '',
      `${m.phone}: ${phone.trim() || '-'}`,
      `${m.email}: ${email.trim() || '-'}`,
      `${m.name}: ${name.trim() || '-'}`,
      `${m.role}: ${t.form.roles[role]}`,
      `${m.reason}: ${reason.trim() || '-'}`,
      '',
      m.outro,
    ].join('\n');
    setMailBody(body);
    setCopied(false);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(m.subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(`${t.mail.subject}\n\n${mailBody}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="home-page fc-site">
      <PublicHeader lang={lang} />

      <section className="home-container">
        <header className="privacy-header">
          <h1>{t.title}</h1>
          <p className="header-subtitle da-subtitle">{t.subtitle}</p>
        </header>

        <div className="privacy-content">
          <section className="privacy-section da-summary">
            <h2>{t.quickTitle}</h2>
            <ul>
              {t.quick.map((q) => <li key={q}>{q}</li>)}
            </ul>
          </section>

          <section className="privacy-section" id="in-app">
            <h2>{t.inAppTitle}</h2>
            <ol className="da-steps">
              {t.inAppSteps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </section>

          <section className="privacy-section" id="request">
            <h2>{t.webTitle}</h2>
            <p>{t.webIntro}</p>

            <form className="da-form" onSubmit={handleSubmit} noValidate>
              <div className="da-grid">
                <label className="da-field">
                  <span>{t.form.phone}</span>
                  <input type="tel" inputMode="tel" autoComplete="tel" value={phone} placeholder={t.form.phonePh} onChange={(e) => setPhone(e.target.value)} />
                </label>
                <label className="da-field">
                  <span>{t.form.email}</span>
                  <input type="email" autoComplete="email" value={email} placeholder={t.form.emailPh} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label className="da-field">
                  <span>{t.form.name}</span>
                  <input type="text" autoComplete="name" value={name} placeholder={t.form.namePh} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="da-field">
                  <span>{t.form.role}</span>
                  <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                    {(Object.keys(t.form.roles) as Role[]).map((r) => (
                      <option key={r} value={r}>{t.form.roles[r]}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="da-field">
                <span>{t.form.reason}</span>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <label className="da-check">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                <span>{t.form.confirm}</span>
              </label>
              {error && <p className="da-error" role="alert">{error}</p>}
              <button type="submit" className="fc-btn fc-btn--primary da-submit">{t.form.submit}</button>
            </form>

            {mailBody && (
              <div className="da-sent" role="status">
                <h3>{t.sentTitle}</h3>
                <p>{t.sentBody}</p>
                <pre>{`${t.mail.subject}\n\n${mailBody}`}</pre>
                <button type="button" className="fc-btn da-copy" onClick={copyBody}>
                  {copied ? t.copied : t.copy}
                </button>
              </div>
            )}

            <h3>{t.altTitle}</h3>
            <p>{t.altBody}</p>
            <div className="contact-info">
              <p><strong>{t.contactLabels.email}:</strong> <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t.mail.subject)}`}>{SUPPORT_EMAIL}</a></p>
              <p><strong>{t.contactLabels.phone}:</strong> <a href={`tel:${SUPPORT_PHONE}`}>{SUPPORT_PHONE}</a></p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>{t.processTitle}</h2>
            <ol className="da-steps">
              {t.process.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </section>

          <section className="privacy-section">
            <h2>{t.deletedTitle}</h2>
            <ul>
              {t.deleted.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </section>

          <section className="privacy-section">
            <h2>{t.keptTitle}</h2>
            <p>{t.keptIntro}</p>
            <ul>
              {t.kept.map((k, i) => <li key={i}>{k}</li>)}
            </ul>
            <p>{t.keptOutro}</p>
          </section>

          <section className="privacy-section">
            <h2>{t.notesTitle}</h2>
            <ul>
              {t.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </section>

          <section className="privacy-section">
            <h2>{t.contactTitle}</h2>
            <div className="contact-info">
              <p><strong>{t.contactLabels.email}:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
              <p><strong>{t.contactLabels.phone}:</strong> <a href={`tel:${SUPPORT_PHONE}`}>{SUPPORT_PHONE}</a></p>
              <p><strong>{t.contactLabels.hours}:</strong> {t.hours}</p>
            </div>
            <p className="da-privacy-link"><Link to={t.privacyTo}>{t.privacyLink} →</Link></p>
          </section>
        </div>
      </section>

      <PublicFooter lang={lang} />
    </div>
  );
};

export default DeleteAccount;
